import { signMessage } from '../handlers/walletActions/wallet'

const BASE_URL = 'https://ai.plug-wallet.com/v1'

export interface Bubble {
  message: string
  emoji: string
}

export interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface ChatContext {
  auth: string
  question: string
  sessionId: string
  userBalances: {
    token: string
    balance: number
    currencySymbol: string
  }[]
  history: Message[]
  principalId: string
  accountId: string
}

const SUPPORTED_FEATURES = [
  'Plug wallet supports Ethereum (ETH) and Ethereum ecosystem coins (ERC-20, most popular EVM chains and their ERC-20)',
  'Plug wallet supports Solana (SOL) and Solana ecosystem coins (SPL - Solana Program Library - coins)',
  'Plug wallet supports Bitcoin (BTC)',
  'Plug wallet supports Internet Computer (ICP) and Internet Computer ecosystem coins',
  'Plug wallet supports ODIN•FUN (Odin) ecosystem coins',
  'Send crypto coins',
  'Send ICP and Bitcoin NFTs',
  'Swap coins on the same FROM and TO chain',
  'Swap coins on differing FROM and TO chains (bridge)',
  'View all your token balances',
  'Discover token balances, find dust',
  'View my activity history - coin transfers, swaps, approvals',
  'Discover dApps, trending coins, Plug news & articles',
  'Buy crypto with fiat (official money)',
  'Stake ICP for nICP',
  'Mint chain-key tokens (ckBTC, ckETH, ckUSDC and many others)',
  'Use Plug for logging into dApps',
  'Top up your ICP canister cycles',
  'Set your preferred currency in the app UI - USD, EUR, GBP, Yen and many others',
  'Save addresses as contacts for frequent transfers',
  'Push notifications for incoming token transfers (only on Mobile)',
  'Add custom ERC-20 (Ethereum and EVMs), SPL (Solana) and ICP tokens',
  'Plugbook lets users choose a unique username that other Plug users can use to send them coins and NFTs',
]

export class PlugIntelligenceClient {
  private static async fetch(
    path: string,
    method: 'GET' | 'POST' = 'GET',
    body?: any,
    auth?: string,
    textStreaming?: boolean
  ) {
    let headers: HeadersInit | undefined = undefined
    if (body || auth) {
      headers = {}

      if (body) headers['Content-Type'] = 'application/json'
      if (auth) headers.Authorization = auth
    }

    return fetch(BASE_URL + path, {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers,
      // @ts-ignore
      reactNative: { textStreaming },
    })
  }

  static async isAvailable(): Promise<boolean> {
    try {
      const response = await this.fetch('/is-available')

      return response.ok
    } catch {
      return false
    }
  }

  static async getInitialMessage(): Promise<string> {
    const response = await this.fetch('/initial-message')

    if (response.ok) {
      return response.text()
    } else {
      throw response
    }
  }

  static async getBubbles(): Promise<Bubble[]> {
    const response = await this.fetch('/bubbles')

    if (response.ok) {
      return response.json()
    } else {
      throw response
    }
  }

  static async chat({ auth, ...context }: ChatContext): Promise<Response> {
    return this.fetch(
      '/chat',
      'POST',
      { ...context, supportedFeatures: SUPPORTED_FEATURES },
      auth,
      true
    )
  }

  static async authenticate(principalId: string): Promise<string> {
    const response = await this.fetch('/auth/nonce', 'POST', { principalId })

    if (response.ok) {
      const { nonce } = await response.json()
      const signedNonce = await signMessage(principalId, nonce)
      const auth = await this.fetch('/auth', 'POST', signedNonce)

      const { jwt } = await auth.json()
      return jwt
    } else {
      throw response
    }
  }
}
